import { Color, Spacing } from '@persona-id/design-tokens';
import { useEffect, useRef, useState } from 'react';
import { CSVLink } from 'react-csv';
import styled from 'styled-components';

import { LoadingShimmerParagraph } from 'dashboard/components/LoadingShimmer/LoadingShimmer';
import ModalWrapper from 'dashboard/components/ModalWrapper';
import { ModalBody, ModalContainer, ModalHeader } from 'dashboard/layouts/ModalLayout';
import { NodeId } from 'dashboard/models/graph';
import {
  GraphExporter,
  GraphExporterType,
  SelectedAccountsGraphExporter,
  SelectedAccountsGraphExporterPreview,
  ShortestPathFromSeedSetGraphExporter,
  ShortestPathFromSeedSetGraphExporterPreview,
} from 'dashboard/models/graph-exporter';
import Button from 'design-system/components/Button';
import { Paragraph } from 'design-system/components/Text';
import hoverableMixin from 'design-system/mixins/hoverable';
import { paragraphMixins } from 'design-system/mixins/text';
import { TransitionDuration } from 'design-system/variables/transitions';
import EventsClient, { GraphEvent } from 'lib/clients/EventsClient';

import { useGraphExplorer } from '../GraphModule/hooks/useGraphExplorer';
import { useGraphQuerying } from '../GraphModule/hooks/useGraphQuerying';
import ReactSelect from '../ReactSelect';

interface GraphExportModalProp {
  exportNodes: NodeId[];
  // showGraphExportModal should never be true if the associated graph query
  // has been redacted or the results of the graph query is null
  showGraphExportModal: boolean;
  hideGraphExportModal: () => void;
}

export const ButtonsContainer = styled('div')`
  display: flex;
  flex-direction: row;
  margin-left: auto;
  margin-right: ${Spacing[2]};
  justify-content: right;
  padding: ${Spacing[4]} ${Spacing[7]} ${Spacing[5]} 0;

  > :first-child {
    margin-right: ${Spacing[2]};
  }
`;

export const StyledCSVDownloadButton = styled(CSVLink)`
  ${paragraphMixins.mdBold}
  height: ${({ theme }) => theme.button.height.md};
  padding: ${Spacing[2]};
  width: auto;
  ${hoverableMixin}
  display: flex;
  align-items: center;
  justify-content: center;

  border: none;
  box-sizing: border-box;
  outline: 0 !important;

  transition: ${TransitionDuration.Fast};
  background-color: ${Color.CorePurple700};
  color: ${Color.PrimaryWhite};
  &:active {
    background-color: ${Color.CorePurple900};
  }

  border-radius: ${({ theme }) => theme.button.borderRadius};
`;

const LoadingShimmerContainer = styled('div')`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  padding-bottom: ${Spacing[5]};
  gap: 10px 20px;
  overflow-y: auto;
`;

const StyledModalBody = styled(ModalBody)`
  max-height: 600px;
  padding-bottom: ${Spacing[2]};
`;

const InvalidExporterContainer = styled('div')`
  display: flex;
  padding-top: ${Spacing[5]};
  gap: 10px 20px;
`;

export const LoadingShimmerTableRow = () => {
  return (
    <LoadingShimmerContainer>
      <LoadingShimmerParagraph size='md' width='100%' />
      <LoadingShimmerParagraph size='md' width='50%' />
      <LoadingShimmerParagraph size='md' width='100%' />
      <LoadingShimmerParagraph size='md' width='70%' />
      <LoadingShimmerParagraph size='md' width='100%' />
    </LoadingShimmerContainer>
  );
};

const preview = (graphExporter: GraphExporter) => {
  switch (graphExporter.type) {
    case GraphExporterType.SelectedAccounts:
      return (
        <SelectedAccountsGraphExporterPreview
          exporter={graphExporter as SelectedAccountsGraphExporter}
        />
      );
    case GraphExporterType.ShortestPathFromSeedSet:
      return (
        <ShortestPathFromSeedSetGraphExporterPreview
          exporter={graphExporter as ShortestPathFromSeedSetGraphExporter}
        />
      );
    default:
      return null;
  }
};

const ExporterSelectorContainer = styled('div')`
  display: flex;
  padding: ${Spacing[4]} ${Spacing[0]} ${Spacing[3]} ${Spacing[0]};
`;

const StyledReactSelect = styled(ReactSelect<GraphExporterType>)`
  width: 50%;
  margin: auto;
  margin-right: 0;
`;

const GraphExportModal = ({
  exportNodes,
  hideGraphExportModal,
  showGraphExportModal,
}: GraphExportModalProp) => {
  const { graphExplorer } = useGraphExplorer();
  const [isLoading, setIsLoading] = useState(true);
  const { getHydratedNodes, currentGraphQuery } = useGraphQuerying();

  // all available exporters
  const exporters = useRef([
    new SelectedAccountsGraphExporter(),
    new ShortestPathFromSeedSetGraphExporter(),
  ]);

  const [selectedExporterType, setSelectedExporterType] = useState(null);
  useEffect(() => {
    if (showGraphExportModal) {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      setSelectedExporterType(exporters.current[0].type);
    } else {
      // on modal close, deselect selected export type
      setSelectedExporterType(null);
    }
  }, [showGraphExportModal, currentGraphQuery]);

  // upon changing selected exporter, set isLoading to true to begin generating export data
  useEffect(() => {
    if (selectedExporterType) {
      setIsLoading(true);
    }
  }, [selectedExporterType]);

  const exportGenerationNonce = useRef<number>(0);
  useEffect(() => {
    if (isLoading) {
      const graphExporter = exporters.current.find(
        (exporter) => exporter.type === selectedExporterType,
      );
      // the nonce is used in case someone switches export options while an exporter is currently
      // generating data. We do not let the stale exporter update the loading state.
      exportGenerationNonce.current++;

      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      if (!graphExporter || graphExporter.invalidMessage(currentGraphQuery)) {
        setIsLoading(false);
        return;
      }
      const currentExport = exportGenerationNonce.current;

      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      graphExporter.generate(
        exportNodes,
        graphExplorer,
        getHydratedNodes,
        () => {
          // we want isLoading to be true initially so that setIsLoading(false) forces a rerender
          // and updates the export preview accordingly
          if (currentExport === exportGenerationNonce.current) {
            setIsLoading(false);
          }
        },
        currentGraphQuery,
      );
    }
  }, [
    isLoading,
    selectedExporterType,
    setIsLoading,
    exportNodes,
    graphExplorer,
    getHydratedNodes,
    currentGraphQuery,
  ]);

  const selectedExporter = exporters.current.find(
    (exporter) => exporter.type === selectedExporterType,
  );
  const selectedExporterInvalidMsg = selectedExporter
    ? /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      selectedExporter.invalidMessage(currentGraphQuery)
    : null;

  return (
    <ModalWrapper
      isOpen={showGraphExportModal}
      handleCloseModal={hideGraphExportModal}
      shouldCloseOnEsc={true}
      shouldCloseOnOverlayClick={true}
    >
      {showGraphExportModal && (
        <ModalContainer
          id='graph-explorer__export-modal'
          size='xl'
          data-test='graph-explorer__export-modal'
        >
          <ModalHeader
            data-test='graph-explorer__export-modal__header'
            onClose={hideGraphExportModal}
          >
            Export Preview
          </ModalHeader>
          <StyledModalBody>
            <ExporterSelectorContainer>
              <Paragraph color='primary' size='md'>
                Export Format:
              </Paragraph>
              <StyledReactSelect
                options={exporters.current.map((exporter: GraphExporter) => {
                  const invalidMsg = exporter.invalidMessage(currentGraphQuery || null);
                  return {
                    label: (
                      <Paragraph color={invalidMsg == null ? 'primary' : 'secondary'}>
                        {exporter.label}
                      </Paragraph>
                    ),
                    value: exporter.type,
                  };
                })}
                value={selectedExporterType}
                onChange={setSelectedExporterType}
                portal={true}
              />
            </ExporterSelectorContainer>
            {isLoading || !selectedExporter ? (
              [...Array(exportNodes.length)].map((_, idx) => {
                return <LoadingShimmerTableRow key={idx} />;
              })
            ) : selectedExporterInvalidMsg == null ? (
              preview(selectedExporter)
            ) : (
              <InvalidExporterContainer>
                <b>Cannot Export Graph Results: </b>
                <Paragraph>{selectedExporterInvalidMsg}</Paragraph>
              </InvalidExporterContainer>
            )}
          </StyledModalBody>
          <ButtonsContainer>
            <Button
              id='graph-explorer__export-cancel'
              data-test='graph-explorer__export-cancel'
              onClick={(e) => {
                hideGraphExportModal();
                // Prevent the canvas underneath from firing a click
                e.stopPropagation();
              }}
              isPending={false}
              width='auto'
              variant='basic'
            >
              Cancel
            </Button>
            {!isLoading && selectedExporter && selectedExporterInvalidMsg == null && (
              <StyledCSVDownloadButton
                variant='primary'
                id='graph-explorer__export-download'
                data-test='graph-explorer__export-download'
                /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                data={selectedExporter.result}
                target='_self'
                onClick={() => {
                  hideGraphExportModal();
                  EventsClient.logAll(GraphEvent.GraphExportDownload);
                }}
                filename={`Graph Export (${new Date().toDateString()}).csv`}
              >
                {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
                {`Download CSV (${selectedExporter.result.length} row${
                  // @ts-expect-error [strict-migration] The following line is not strict; please fix!
                  selectedExporter.result.length === 1 ? '' : 's'
                })`}
              </StyledCSVDownloadButton>
            )}
          </ButtonsContainer>
        </ModalContainer>
      )}
    </ModalWrapper>
  );
};

export default GraphExportModal;
